import { Alert, Checkbox, Dropdown, Flex, Text } from "@fluentui/react-northstar";
import React, { useCallback, useMemo } from "react";

import {
  RRule,
  RRuleByDay,
  RRuleFrequency,
  getByDayLabel,
  getPositionLabel,
  parseRRule,
  rruleToHumanizedString,
} from "../../../../../_shared/utils/recurringHelpers";
import { capitalizeFirstLetter } from "../../../../../_shared/utils/teamsHelpers";
import { ButtonWeekDay } from "./ButtonWeekDay";

function getNumberArray(start: number, stop: number): number[] {
  const data = [];
  for (let i = start; i <= stop; i++) {
    data.push(i);
  }
  return data;
}

const RRuleFrequencySelector: React.FC<{
  onRRuleChangeHandler: (rule: RRule) => void;
  rule: RRule;
}> = ({ onRRuleChangeHandler, rule }) => {
  const freqDropdownOnchange = useCallback(
    (_e, props) => {
      if (props?.value) {
        const newFreq = props.value as RRuleFrequency;
        switch (newFreq.toLowerCase()) {
          case "daily":
            onRRuleChangeHandler({ freq: "DAILY", interval: 1 });
            break;
          case "weekly":
            onRRuleChangeHandler({
              freq: "WEEKLY",
              interval: 1,
              byday: ["MO", "TU", "WE", "TH", "FR"],
            });
            break;
          case "monthly":
            onRRuleChangeHandler({ freq: "MONTHLY", interval: 1, bymonthday: 1 });
            break;
        }
      }
    },
    [onRRuleChangeHandler]
  );

  return (
    <>
      <Dropdown
        items={["Daily", "Weekly", "Monthly"]}
        fluid
        value={capitalizeFirstLetter(rule.freq.toLowerCase())}
        onChange={freqDropdownOnchange}
        getA11ySelectionMessage={{
          onAdd: item => `${item} has been selected.`,
        }}
      />

      <Text content="every" />

      <Dropdown
        items={getNumberArray(1, 30)}
        fluid
        value={rule.interval}
        onChange={(_, d) => onRRuleChangeHandler({ ...rule, interval: d.value as number })}
      />

      <Text
        content={`${
          rule.freq === "DAILY"
            ? "day"
            : rule.freq === "WEEKLY"
            ? "week"
            : rule.freq === "MONTHLY"
            ? "month"
            : "year"
        }${rule.interval && rule.interval > 1 ? "s" : ""}`}
      />
    </>
  );
};

const WeekDaysSelector: React.FC<{
  rule: RRule;
  onRRuleChangeHandler: (rule: RRule) => void;
}> = ({ onRRuleChangeHandler, rule }) => {
  return (
    <Flex.Item align="stretch">
      <Flex hAlign="center">
        <Flex gap="gap.smaller">
          <ButtonWeekDay day="SU" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="MO" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="TU" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="WE" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="TH" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="FR" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
          <ButtonWeekDay day="SA" rule={rule} onRRuleChangeHandler={onRRuleChangeHandler} />
        </Flex>
      </Flex>
    </Flex.Item>
  );
};

const MonthDaysSelector: React.FC<{ onRRuleChangeHandler: (rule: RRule) => void; rule: RRule }> = ({
  onRRuleChangeHandler,
  rule,
}) => {
  return (
    <>
      <Flex column gap="gap.medium">
        <Flex gap="gap.small" vAlign="center">
          <Checkbox
            checked={!!rule.bymonthday}
            onChange={() => {
              onRRuleChangeHandler({ freq: rule.freq, bymonthday: 1 });
            }}
          />

          <Text content="on day" size="large" />
          <Dropdown
            items={getNumberArray(1, 31)}
            fluid
            value={rule.bymonthday}
            disabled={!rule.bymonthday}
            onChange={(_, d) => {
              onRRuleChangeHandler({ ...rule, bymonthday: d.value as number });
            }}
          />
        </Flex>

        <Flex gap="gap.small" vAlign="center">
          <Checkbox
            checked={!!rule.bysetpos}
            onChange={() => {
              onRRuleChangeHandler({ freq: rule.freq, bysetpos: 1, byday: ["MO"] });
            }}
          />

          <Text content="on the" size="large" style={{ whiteSpace: "nowrap" }} />

          <Dropdown
            items={[
              { header: getPositionLabel(1), value: 1 },
              { header: getPositionLabel(2), value: 2 },
              { header: getPositionLabel(3), value: 3 },
              { header: getPositionLabel(4), value: 4 },
              { header: getPositionLabel(-1), value: -1 },
            ]}
            fluid
            disabled={!rule.bysetpos}
            value={getPositionLabel(rule.bysetpos as number)}
            onChange={(_, d) => {
              onRRuleChangeHandler({ ...rule, bysetpos: (d.value as { value: number }).value });
            }}
          />

          <Dropdown
            items={[
              { header: getByDayLabel("MO"), value: "MO" as RRuleByDay },
              { header: getByDayLabel("TU"), value: "TU" as RRuleByDay },
              { header: getByDayLabel("WE"), value: "WE" as RRuleByDay },
              { header: getByDayLabel("TH"), value: "TH" as RRuleByDay },
              { header: getByDayLabel("FR"), value: "FR" as RRuleByDay },
              { header: getByDayLabel("SA"), value: "SA" as RRuleByDay },
              { header: getByDayLabel("SU"), value: "SU" as RRuleByDay },
            ]}
            disabled={!rule.byday}
            fluid
            value={rule.byday ? getByDayLabel(rule.byday[0]) : undefined}
            onChange={(_, d) => {
              onRRuleChangeHandler({ ...rule, byday: [(d.value as { value: RRuleByDay }).value] });
            }}
          />
        </Flex>
      </Flex>
    </>
  );
};

export const RRuleField: React.FC<{
  ruleString: string;
  onRRuleChangeHandler: (rule: RRule) => void;
}> = ({ ruleString, onRRuleChangeHandler }) => {
  const rule = useMemo(() => parseRRule(ruleString), [ruleString]);

  return (
    <Flex column gap="gap.medium">
      <Flex gap="gap.small" vAlign="center">
        <RRuleFrequencySelector onRRuleChangeHandler={onRRuleChangeHandler} rule={rule} />
      </Flex>

      {rule.freq === "WEEKLY" && (
        <WeekDaysSelector onRRuleChangeHandler={onRRuleChangeHandler} rule={rule} />
      )}

      {rule.freq === "MONTHLY" && (
        <MonthDaysSelector onRRuleChangeHandler={onRRuleChangeHandler} rule={rule} />
      )}

      <Alert
        styles={{ marginTop: "1rem" }}
        content={
          <Text align="center" content={rruleToHumanizedString(ruleString)} weight="light" />
        }
      />
    </Flex>
  );
};
